import useApi from '@/api'
import { getProfilePath } from '@/common'
import { profilesAdd, validateFolders } from '@/logic/profiles'
import { stateIsEmpty, stateIsValidPublicProfile, stateSetImported } from '@/logic/stateReducers'
import Store from '@/store'
import EditingContext from '@/store/editingContext'
import Session from '@/store/session'
import { ProfileType } from '@/types/api'
import Select, { underlinerFunc } from '@/ui/select'
import { faFolder, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { entryFromExport, exportProfile } from './common'
import { publicFromProfile, PublicOptions, PublicSection } from './public-section'

type FolderOption = {
  id: number
  name: string
  depth: number
}

const renderFolder = ({ name, depth }: FolderOption, search?: RegExp) => (
  <span style={{ paddingLeft: 8 * depth }}>
    <FontAwesomeIcon icon={faFolder} /> {search ? underlinerFunc(name, search) : name}
  </span>
)

const FolderSelect = React.memo(function FolderSelect({
  folder,
  setFolder
}: {
  folder: number
  setFolder: (id: number) => void
}) {
  const loggedIn = Session.useSelector((store) => store.user != null)
  const folders = Session.useSelector((store) => store.folders)
  const dispatch = Session.useDispatch()
  const api = useApi()

  const foldersLoaded = React.useRef(false)
  React.useEffect(() => {
    if (foldersLoaded.current) return
    foldersLoaded.current = true
    if (!loggedIn || folders) return
    api.folders().then(
      (result) => dispatch.setFolders(validateFolders(result)),
      () => null
    )
  }, [api, dispatch, folders, loggedIn])

  const getOptions = React.useCallback(() => {
    if (!folders) return []
    const children: { [key: number]: number[] | undefined } = {}
    for (const id in folders) {
      const parent = folders[id].parent
      children[parent] = children[parent] ?? []
      children[parent]!.push(parseInt(id))
    }
    const options: FolderOption[] = []
    function traverse(id: number, depth: number) {
      options.push({
        id,
        name: id === 0 ? `My Profiles` : folders![id].name,
        depth
      })
      if (children[id]) {
        for (const child of children[id]!) {
          traverse(child, depth + 1)
        }
      }
    }
    traverse(0, 0)
    return options
  }, [folders])

  const onChange = React.useCallback((opt?: FolderOption) => opt && setFolder(opt.id), [setFolder])

  if (!folders) return null

  const selected = folders[folder]
  const value = selected ? { id: folder, name: selected.name, depth: 0 } : { id: 0, name: `My Profiles`, depth: 0 }

  return (
    <Select
      options={getOptions}
      renderOption={renderFolder}
      value={value}
      onChange={onChange}
      className="poe-save-folder"
    />
  )
})

export default function SaveSection({ type }: { type: ProfileType }) {
  const [status, setStatus] = EditingContext.useState('saveStatus')

  const [saveName, setSaveName] = React.useState('')
  const [update, setUpdate] = React.useState(true)
  const imported = Store.useSelector((state) => state.imported)
  const planner = Store.useSelector((state) => state.planner)

  const user = Session.useSelector((state) => state.user)
  const [publicOptions, setPublic] = React.useState<PublicOptions>()

  React.useEffect(() => {
    setPublic(imported?.public && imported?.user?.id === user?.id ? publicFromProfile(imported, user) : undefined)
  }, [imported, user])

  const onTogglePublic = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        const options: PublicOptions = {}
        if (imported?.public) {
          Object.assign(options, publicFromProfile(imported, user))
        }
        setPublic(options)
      } else {
        setPublic(undefined)
      }
    },
    [imported, user]
  )

  const [saving, setSaving] = React.useState(false)
  const [folder, setFolder] = React.useState(0)

  const isEmpty = Store.useSelector((state) => stateIsEmpty(state, type))
  const state = Store.useState()
  const isValidPublicProfile = React.useMemo(
    () => stateIsValidPublicProfile({ state, type, publicOptions }),
    [state, type, publicOptions]
  )
  const isSavingDisabled = isEmpty || (publicOptions != null && !isValidPublicProfile) || saving

  const dispatch = Store.useDispatch()
  const mainStore = Store.useStore()
  const api = useApi()

  const sessionDispatch = Session.useDispatch()

  const onSave = React.useCallback(() => {
    let state = mainStore.getState()
    if (state.imported?.user && state.imported.user.id !== user?.id && state.planner?.author) {
      state = { ...state }
      state.planner = { ...state.planner! }
      delete state.planner.author
      dispatch.setState(state)
    }
    const data = exportProfile(state, saveName, user ? publicOptions : undefined, type)
    if (update) {
      if (state.imported?.user && state.imported.user.id === user?.id) {
        data.id = state.imported.id
      }
    }
    if (folder) data.folder = folder
    setSaving(true)
    setStatus(undefined)
    api
      .save(data)
      .then(
        (id) => {
          const entry = entryFromExport(data, id, user)
          const url = new URL(getProfilePath(id, type), window.location.href)
          setStatus(url.href)
          sessionDispatch.setProfiles((profiles) => profilesAdd(profiles, entry))
          dispatch.setState((state) => stateSetImported(state, entry))
        },
        (error) => setStatus(error.message)
      )
      .finally(() => setSaving(false))
  }, [saveName, mainStore, api, setStatus, dispatch, sessionDispatch, folder, type, publicOptions, update, user])

  return (
    <div className="poe-SectionContent poe-section-save">
      <div className="poe-description">
        {`Enter a name or a short description for your profile. It will show in the page title when the profile is loaded.`}
      </div>
      <div className="poe-import-options">
        <div className="poe-save-options">
          <FolderSelect folder={folder} setFolder={setFolder} />
        </div>

        {!!(imported && imported.user && imported.user.id === user?.id) && (
          <div className="poe-save-options">
            <label>
              <input type="checkbox" checked={update} onChange={(e) => setUpdate(e.target.checked)} />
              Update existing profile
            </label>
          </div>
        )}

        {!!planner &&
          type === 'planner' &&
          (user ? (
            <div className="poe-save-options">
              <label>
                <input type="checkbox" checked={publicOptions != null} onChange={onTogglePublic} />
                Public build
              </label>
            </div>
          ) : (
            <div className="poe-save-options">
              Log in using your Maxroll account to create public builds that can be browsed and rated by other users
            </div>
          ))}
        {!!(planner && publicOptions && user && type === 'planner') && (
          <PublicSection profile={planner} options={publicOptions} setOptions={setPublic} />
        )}

        <input
          type="text"
          className="poe-save-name"
          value={
            isEmpty
              ? 'Cannot save empty profile'
              : publicOptions != null && !isValidPublicProfile
              ? 'Cannot list public profile without any skills, equipment or endgame/leveling tags.'
              : saveName
          }
          onChange={(e) => setSaveName(e.target.value)}
          disabled={isSavingDisabled}
        />

        <button onClick={onSave} disabled={isEmpty || saving}>
          {saving && <FontAwesomeIcon icon={faSpinner} spin />}
          {saving ? `Saving` : `Save`}
        </button>
      </div>
      {!!status && (
        <div className="poe-save-status">
          <input type="text" value={status} readOnly onFocus={(e) => e.target.select()} />
        </div>
      )}
    </div>
  )
}
